import type { Middleware } from 'redux';
import { logout, tokenRefreshSuccess } from '../store/slices/authSlice';
import { API_URLS } from './constants';

type TokenRefreshPayload = { access: string; refresh?: string };

/** One in-flight refresh at a time so parallel thunks do not double-use a refresh token before rotation blacklists it. */
let refreshInFlight: Promise<TokenRefreshPayload | null> | null = null;

// Helper to decode JWT and check expiration
const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;

    if (!exp) return false; // No expiration claim

    // Check if token expires in less than 5 minutes
    const now = Date.now() / 1000;
    return exp < now + 300; // 5 minutes buffer
  } catch {
    return true;
  }
};

function refreshTokens(refreshToken: string): Promise<TokenRefreshPayload | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async (): Promise<TokenRefreshPayload | null> => {
    try {
      const response = await fetch(API_URLS.REFRESH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (!data?.access || typeof data.access !== 'string') {
        return null;
      }

      const payload: TokenRefreshPayload = { access: data.access };
      if (typeof data.refresh === 'string' && data.refresh.length > 0) {
        payload.refresh = data.refresh;
      }
      return payload;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// Middleware to handle token refresh
export const authMiddleware: Middleware =
  (store: any) => (next: any) => async (action: any) => {
    const actionType: string | undefined = action?.type;
    const isLoginFlow = actionType && actionType.startsWith('api/login');

    if (!isLoginFlow && actionType && actionType.endsWith('/pending')) {
      const state = store.getState() as any;
      const { accessToken, refreshToken } = state.auth || {};

      if (accessToken && refreshToken && isTokenExpired(accessToken)) {
        const tokens = await refreshTokens(refreshToken);

        if (tokens) {
          store.dispatch(
            tokenRefreshSuccess({
              access: tokens.access,
              ...(tokens.refresh ? { refresh: tokens.refresh } : {}),
            })
          );
        } else {
          store.dispatch(logout());
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }
      }
    }

    const result = next(action);

    if (!isLoginFlow && actionType && actionType.endsWith('/rejected')) {
      const error = action?.payload;
      if (error?.status === 401 || error?.data?.code === 'token_not_valid') {
        const state = store.getState() as any;
        const { refreshToken } = state.auth || {};

        if (refreshToken) {
          const tokens = await refreshTokens(refreshToken);

          if (tokens) {
            store.dispatch(
              tokenRefreshSuccess({
                access: tokens.access,
                ...(tokens.refresh ? { refresh: tokens.refresh } : {}),
              })
            );

            const originalActionType = actionType.replace('/rejected', '/pending');
            const baseActionType = originalActionType.replace('/pending', '');

            if (action.meta?.arg !== undefined) {
              const retryAction = {
                type: baseActionType,
                payload: action.meta.arg,
              };

              setTimeout(() => {
                store.dispatch(retryAction as any);
              }, 100);
            }
          } else {
            store.dispatch(logout());
            if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        } else {
          store.dispatch(logout());
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
    }

    return result;
  };
